import { useState } from 'react'
import { Button, Input, Modal, Select, Textarea, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { useCaseOptions, useCreateDeadline, useLawyers } from '../hooks'
import type { Deadline } from '../types'

export interface NewDeadlineModalProps {
  open: boolean
  onClose: () => void
  onCreated?: (deadline: Deadline) => void
}

const NO_CASE = 'none'

/** „Dodaj termin" — ręczny wpis (`source=manual`). */
export function NewDeadlineModal({ open, onClose, onCreated }: NewDeadlineModalProps) {
  const toast = useToast()
  const lawyers = useLawyers(open)
  const cases = useCaseOptions(open)
  const create = useCreateDeadline()

  const [caseId, setCaseId] = useState(NO_CASE)
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [legalBasis, setLegalBasis] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setCaseId(NO_CASE)
    setTitle('')
    setDueDate('')
    setOwnerId('')
    setLegalBasis('')
    setNote('')
    setError(null)
  }

  const close = () => {
    reset()
    onClose()
  }

  const submit = async () => {
    setError(null)
    if (!title.trim() || !dueDate) {
      setError('Podaj tytuł i datę terminu.')
      return
    }
    try {
      const created = await create.mutateAsync({
        case_id: caseId === NO_CASE ? null : Number(caseId),
        title: title.trim(),
        due_date: dueDate,
        owner_id: ownerId ? Number(ownerId) : null,
        legal_basis: legalBasis.trim() ? legalBasis.trim() : null,
        note: note.trim() ? note.trim() : null,
      })
      toast.success('Termin dodany')
      onCreated?.(created)
      reset()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się dodać terminu.')
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Nowy termin"
      subtitle="Termin wprowadzony ręcznie — źródło pozycji na liście: ręcznie."
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={create.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            Dodaj termin
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Select
            label="Sprawa"
            value={caseId}
            onChange={(event) => setCaseId(event.target.value)}
            options={[
              { value: NO_CASE, label: 'brak przypisania (wymaga dekretacji)' },
              ...(cases.data?.items ?? []).map((item) => ({
                value: String(item.id),
                label: `${item.signature} · ${item.client_name}`,
              })),
            ]}
          />
        </div>
        <div className="col-span-2">
          <Input
            label="Tytuł terminu"
            value={title}
            placeholder="np. Odpowiedź na apelację"
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <Input
          label="Data terminu"
          type="date"
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
        />
        <Select
          label="Właściciel"
          value={ownerId}
          onChange={(event) => setOwnerId(event.target.value)}
          options={[
            { value: '', label: 'nieprzypisany' },
            ...(lawyers.data ?? []).map((lawyer) => ({
              value: String(lawyer.id),
              label: lawyer.full_name,
            })),
          ]}
        />
        <div className="col-span-2">
          <Input
            label="Podstawa prawna / źródło"
            value={legalBasis}
            placeholder="np. art. 372 k.p.c. · 14 dni"
            onChange={(event) => setLegalBasis(event.target.value)}
          />
        </div>
        <div className="col-span-2">
          <Textarea
            label="Notatka"
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </div>
      {error ? <p className="mt-3 text-micro text-danger">{error}</p> : null}
    </Modal>
  )
}
